/**
 * CategoryEditor Drawer (Uber Eats Style)
 */

import React, { useEffect, useState } from "react";
import { Save, Trash2, RefreshCw } from "lucide-react";
import { DishCategory } from "@tasty/api";
import { DishCategoryClient } from "../../api-client";
import { TERMS } from "@tasty/shared";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface CategoryEditorDrawerProps {
  category: DishCategory | null;
  restaurantId: string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export function CategoryEditorDrawer({
  category,
  restaurantId,
  onClose,
  onSaved,
  onDeleted,
}: CategoryEditorDrawerProps) {
  const isEditMode = Boolean(category);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(category?.name || "");
  }, [category]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (isEditMode && category) {
        await DishCategoryClient.updateItem({ id: category.id, name: name.trim() });
      } else {
        await DishCategoryClient.createItem({ 
          name: name.trim(), 
          restaurantID: restaurantId,
          order: 0 
        });
      }
      toast.success(isEditMode ? "Kategorie aktualisiert" : "Kategorie erstellt");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SheetContent className="flex flex-col h-full sm:max-w-md p-0 overflow-hidden border-l shadow-2xl">
      <SheetHeader className="p-6 border-b shrink-0 bg-muted/5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <SheetTitle>{isEditMode ? "Kategorie bearbeiten" : "Neue Kategorie"}</SheetTitle>
            <SheetDescription>Strukturiere deinen {TERMS.catalog}</SheetDescription>
          </div>
          <Button onClick={handleSave} disabled={saving || !name.trim()} size="sm">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </SheetHeader>

      <div className="p-6 space-y-6">
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Kategorie Name</label>
          <Input 
            value={name} 
            onChange={e => setName(e.target.value)}
            placeholder="z.B. Vorspeisen"
            className="h-12 rounded-xl bg-muted/5 border-muted-foreground/10 font-bold"
          />
        </div>

        {isEditMode && (
          <Button 
            variant="ghost" 
            className="w-full text-destructive hover:bg-destructive/10 mt-8 font-bold uppercase tracking-widest text-[10px]"
            onClick={() => {
              if (confirm(`Kategorie löschen? ${TERMS.productPlural} bleiben erhalten aber sind dann unkategorisiert.`)) {
                DishCategoryClient.deleteItem({ id: category!.id }).then(onDeleted);
              }
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Kategorie löschen
          </Button>
        )}
      </div>
    </SheetContent>
  );
}

